// src/modules/settings/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import { Card, Tabs, Form, Button, Space, message, Spin, Alert } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { siteApi, SiteConfigUpdate } from '@/services/api/site';

// Импорты компонентов
import { TimersSettings } from './components/TimersSettings';
import { OrdersSettings } from './components/OrdersSettings';
import { WorkingHoursSettings } from './components/WorkingHoursSettings';
import { ContactsSettings } from './components/ContactsSettings';
import { LegalTextsSettings } from './components/LegalTextsSettings';
import { FeedbackSettings } from './components/FeedbackSettings'; // ✅ ДОБАВЛЕНО

export const SettingsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('timers');
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await siteApi.getConfig();
      const data = response.data.data;

      const formValues = {
        // Таймеры
        cart_timer_minutes: data.timers.cart_minutes,
        order_timer_minutes: data.timers.order_minutes,
        payment_halyk_timer_minutes: data.timers.payment_halyk_minutes,
        payment_manual_timer_hours: data.timers.payment_manual_hours,
        
        // Заказы
        minimum_order_amount: data.orders.minimum_order_amount,
        is_minimum_order_enabled: data.orders.is_minimum_order_enabled,
        delivery_time_slots: JSON.stringify(
          data.orders.delivery_time_slots || [
            { value: "09:00-14:00", label: "09:00 - 14:00" },
            { value: "16:00-21:00", label: "16:00 - 21:00" }
          ], 
          null, 
          2
        ),
        
        // Режим работы
        working_mode: data.working.mode,
        working_hours_ru: data.working.hours_ru || '',
        working_hours_kz: data.working.hours_kz || '',
        working_hours_en: data.working.hours_en || '',
        
        // Контакты - используем дефолты из бэкенда
        order_phone: data.contacts.order_phone || '+77784789088',
        contact_phone: data.contacts.contact_phone || '+77784789088',
        contact_email: data.contacts.contact_email || 'info@domchef.kz',
        facebook_url: data.contacts.facebook_url || 'https://facebook.com/domchefkz',
        instagram_url: data.contacts.instagram_url || 'https://instagram.com/domchefkz',
        threads_url: data.contacts.threads_url || 'https://threads.net/@domchefkz',
        tiktok_url: data.contacts.tiktok_url || 'https://tiktok.com/@domchefkz',
        
        // Юридические тексты
        delivery_terms_ru: data.legal.delivery_terms_ru || '',
        delivery_terms_kz: data.legal.delivery_terms_kz || '',
        delivery_terms_en: data.legal.delivery_terms_en || '',
        payment_terms_ru: data.legal.payment_terms_ru || '',
        payment_terms_kz: data.legal.payment_terms_kz || '',
        payment_terms_en: data.legal.payment_terms_en || '',
        how_to_order_ru: data.legal.how_to_order_ru || '',
        how_to_order_kz: data.legal.how_to_order_kz || '',
        how_to_order_en: data.legal.how_to_order_en || '',
        privacy_policy_ru: data.legal.privacy_policy_ru || '',
        privacy_policy_kz: data.legal.privacy_policy_kz || '',
        privacy_policy_en: data.legal.privacy_policy_en || '',
        terms_of_use_ru: data.legal.terms_of_use_ru || '',
        terms_of_use_kz: data.legal.terms_of_use_kz || '',
        terms_of_use_en: data.legal.terms_of_use_en || '',
        ambassador_offer_ru: data.legal.ambassador_offer_ru || '',
        ambassador_offer_kz: data.legal.ambassador_offer_kz || '',
        ambassador_offer_en: data.legal.ambassador_offer_en || '',
        
        // ✅ ДОБАВЛЕНО: Настройки отзывов
        feedback_enabled: data.feedback.enabled,
        feedback_delay_hours: data.feedback.delay_hours,
        feedback_reminder_hours: data.feedback.reminder_hours,
        feedback_max_attempts: data.feedback.max_attempts,
        auto_approve_high_ratings: data.feedback.auto_approve_high_ratings,
      };
      
    form.setFieldsValue(formValues);
    setInitialValues(formValues);
    setHasChanges(false);
    
    // Снимаем флаг загрузки с небольшой задержкой, чтобы форма успела стабилизироваться
    setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
  } catch (error: any) {
    console.error('Error loading settings:', error);
    message.error('Ошибка загрузки настроек');
  } finally {
    setLoading(false);
  }
};

  // Отслеживание изменений формы
  const handleFormChange = () => {
    // Игнорируем изменения во время первой загрузки
    if (isInitialLoad) {
      return;
    }
    
    // Игнорируем если initialValues ещё не установлены
    if (!initialValues) {
      return;
    }
    
    const currentValues = form.getFieldsValue();
    
    // Нормализуем значения для сравнения
    const normalizeForCompare = (obj: any) => {
      const normalized: any = {};
      for (const key in obj) {
        let value = obj[key];
        // Приводим пустые строки и null к одному виду
        if (value === '' || value === null || value === undefined) {
          normalized[key] = null;
        } else if (typeof value === 'string') {
          normalized[key] = value.trim();
        } else {
          normalized[key] = value;
        }
      }
      return normalized;
    };
    
    const normalizedCurrent = normalizeForCompare(currentValues);
    const normalizedInitial = normalizeForCompare(initialValues || {});
    
    const changed = JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial);
    setHasChanges(changed);
  };

  // ДОБАВЬ эту функцию в SettingsPage
  const forceCheckChanges = () => {
    handleFormChange();
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: берём ВСЕ значения напрямую из формы
    // а не из параметра values, который может содержать undefined для неактивных табов
    const allValues = form.getFieldsValue();
    
    console.log('=== НАЧАЛО SUBMIT ===');
    console.log('Values from parameter:', values);
    console.log('All values from form:', allValues);
    console.log('Initial values:', initialValues);
    
    try {
      const normalize = (value: any) => {
        // Приводим null, undefined и пустую строку к null
        if (value === null || value === undefined || value === '') return null;
        // Для строк убираем пробелы по краям
        if (typeof value === 'string') return value.trim();
        return value;
      };
  
      const hasChanged = (field: string) => {
        // ИСПРАВЛЕНО: используем allValues вместо values
        const current = normalize(allValues[field]);
        const initial = normalize(initialValues?.[field]);
        
        // КРИТИЧЕСКАЯ ЗАЩИТА: если поле undefined в allValues но есть в initialValues
        // это значит компонент не отрендерился - игнорируем такое "изменение"
        if (allValues[field] === undefined && initialValues?.[field] !== undefined && initialValues?.[field] !== null && initialValues?.[field] !== '') {
          console.log(`[SKIP] "${field}" is undefined in form but has value in initialValues - component not rendered`);
          return false;
        }
        
        // Для JSON полей сравниваем как строки
        if (field === 'delivery_time_slots') {
          const currentStr = typeof current === 'string' ? current : JSON.stringify(current);
          const initialStr = typeof initial === 'string' ? initial : JSON.stringify(initial);
          const changed = currentStr !== initialStr;
          
          if (changed) {
            console.log(`[CHANGE] "${field}":`, {
              initial: initialStr?.substring(0, 50),
              current: currentStr?.substring(0, 50)
            });
          }
          return changed;
        }
        
        // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: если оба значения null/пустые - это НЕ изменение!
        if ((current === null || current === '') && (initial === null || initial === '')) {
          return false;
        }
        
        const changed = current !== initial;
        
        // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ
        if (changed) {
          console.log(`[CHANGE] "${field}":`, {
            initialType: typeof initialValues?.[field],
            currentType: typeof allValues[field],
            initialValue: initialValues?.[field],
            currentValue: allValues[field],
            initialNorm: initial,
            currentNorm: current
          });
        }
        
        return changed;
      };
  
      // Парсим JSON для временных слотов
      let parsedSlots;
      if (hasChanged('delivery_time_slots')) {
        try {
          parsedSlots = allValues.delivery_time_slots ? JSON.parse(allValues.delivery_time_slots) : [];
        } catch (e) {
          message.error('Неверный формат JSON для временных слотов');
          setSaving(false);
          return;
        }
      }
  
      // Формируем payload только с измененными полями
      const payload: Partial<SiteConfigUpdate> = {};
      
      // Таймеры
      if (hasChanged('cart_timer_minutes')) {
        payload.cart_timer_minutes = allValues.cart_timer_minutes;
      }
      if (hasChanged('order_timer_minutes')) {
        payload.order_timer_minutes = allValues.order_timer_minutes;
      }
      if (hasChanged('payment_halyk_timer_minutes')) {
        payload.payment_halyk_timer_minutes = allValues.payment_halyk_timer_minutes;
      }
      if (hasChanged('payment_manual_timer_hours')) {
        payload.payment_manual_timer_hours = allValues.payment_manual_timer_hours;
      }
      
      // Заказы
      if (hasChanged('minimum_order_amount')) {
        payload.minimum_order_amount = allValues.minimum_order_amount;
      }
      if (hasChanged('is_minimum_order_enabled')) {
        payload.is_minimum_order_enabled = allValues.is_minimum_order_enabled;
      }
      if (hasChanged('delivery_time_slots')) {
        payload.delivery_time_slots = parsedSlots;
      }
      
      // Режим работы
      if (hasChanged('working_mode')) {
        payload.working_mode = allValues.working_mode;
      }
      if (hasChanged('working_hours_ru')) {
        payload.working_hours_ru = allValues.working_hours_ru || '';
      }
      if (hasChanged('working_hours_kz')) {
        payload.working_hours_kz = allValues.working_hours_kz || '';
      }
      if (hasChanged('working_hours_en')) {
        payload.working_hours_en = allValues.working_hours_en || '';
      }
      
      // Контакты
      if (hasChanged('order_phone')) {
        payload.order_phone = allValues.order_phone || '';
      }
      if (hasChanged('contact_phone')) {
        payload.contact_phone = allValues.contact_phone || '';
      }
      if (hasChanged('contact_email')) {
        payload.contact_email = allValues.contact_email || '';
      }
      if (hasChanged('facebook_url')) {
        payload.facebook_url = allValues.facebook_url || '';
      }
      if (hasChanged('instagram_url')) {
        payload.instagram_url = allValues.instagram_url || '';
      }
      if (hasChanged('threads_url')) {
        payload.threads_url = allValues.threads_url || '';
      }
      if (hasChanged('tiktok_url')) {
        payload.tiktok_url = allValues.tiktok_url || '';
      }
      
      // Юридические тексты
      const legalFields = [
        'delivery_terms_ru', 'delivery_terms_kz', 'delivery_terms_en',
        'payment_terms_ru', 'payment_terms_kz', 'payment_terms_en',
        'how_to_order_ru', 'how_to_order_kz', 'how_to_order_en',
        'privacy_policy_ru', 'privacy_policy_kz', 'privacy_policy_en',
        'terms_of_use_ru', 'terms_of_use_kz', 'terms_of_use_en',
        'ambassador_offer_ru', 'ambassador_offer_kz', 'ambassador_offer_en',
      ];
      
      legalFields.forEach(field => {
        if (hasChanged(field)) {
          (payload as any)[field] = allValues[field] || '';
        }
      });

      // ✅ ДОБАВЛЕНО: Настройки отзывов
      if (hasChanged('feedback_enabled')) {
        payload.feedback_enabled = allValues.feedback_enabled;
      }
      if (hasChanged('feedback_delay_hours')) {
        payload.feedback_delay_hours = allValues.feedback_delay_hours;
      }
      if (hasChanged('feedback_reminder_hours')) {
        payload.feedback_reminder_hours = allValues.feedback_reminder_hours;
      }
      if (hasChanged('feedback_max_attempts')) {
        payload.feedback_max_attempts = allValues.feedback_max_attempts;
      }
      if (hasChanged('auto_approve_high_ratings')) {
        payload.auto_approve_high_ratings = allValues.auto_approve_high_ratings;
      }
  
      console.log('=== PAYLOAD (ТОЛЬКО ИЗМЕНЕНИЯ) ===');
      console.log(JSON.stringify(payload, null, 2));
      console.log(`Изменено полей: ${Object.keys(payload).length}`);
      
      if (Object.keys(payload).length === 0) {
        message.info('Нет изменений для сохранения');
        setSaving(false);
        return;
      }
      
      await siteApi.updateConfig(payload);
      
      message.success('Настройки успешно сохранены');
      
      // ИСПРАВЛЕНО: Обновляем initialValues ПОСЛЕ успешного сохранения
      const updatedValues = form.getFieldsValue();
      setInitialValues(updatedValues);
      setHasChanges(false);
      
    } catch (error: any) {
      console.error('=== ОШИБКА СОХРАНЕНИЯ ===');
      console.error('Полная ошибка:', error);
      
      const errorMsg = error?.response?.data?.error?.message || 'Ошибка сохранения настроек';
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(initialValues);
    setHasChanges(false);
    setResetKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'timers',
      label: '⏱️ Таймеры',
      children: <TimersSettings />,
      forceRender: true,
    },
    {
      key: 'orders',
      label: '📦 Заказы',
      children: <OrdersSettings key={resetKey} onSlotsChange={forceCheckChanges} />,
      forceRender: true,
    },
    {
      key: 'working',
      label: '🕐 Режим работы',
      children: <WorkingHoursSettings />,
      forceRender: true,
    },
    {
      key: 'contacts',
      label: '📞 Контакты',
      children: <ContactsSettings />,
      forceRender: true,
    },
    {
      key: 'legal',
      label: '📄 Юридические тексты',
      children: <LegalTextsSettings />,
      forceRender: true,
    },
    // ✅ ДОБАВЛЕНО: Вкладка отзывов
    {
      key: 'feedback',
      label: '⭐ Отзывы',
      children: <FeedbackSettings />,
      forceRender: true,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>Настройки системы</h1>
        <Alert
          message="Внимание"
          description="Изменения в настройках применяются сразу после сохранения и влияют на всех пользователей сайта."
          type="warning"
          showIcon
          closable
        />
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            tabBarStyle={{ marginBottom: 24 }}
          />

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saving}
                disabled={!hasChanges || saving}
                icon={<SaveOutlined />}
                size="large"
              >
                Сохранить все изменения
              </Button>
              <Button 
                onClick={handleReset}
                disabled={!hasChanges || saving}
                icon={<ReloadOutlined />}
                size="large"
              >
                Отменить изменения
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};